import { FormattedMessage } from "react-intl";
import { useTranslation } from "react-i18next";
import { Fragment } from "react";
import { connect } from "react-redux";
import { bindActionCreators, type Dispatch } from "redux";
import { createSelector } from "reselect";
import styled from "styled-components";

import { LocalStorageKey } from "shared/types";
import { localStorage } from "shared/utils";

import { changeLocale } from "../../containers/LanguageProvider/actions";
import {
  makeSelectLocale,
  type LanguageRootState
} from "../../containers/LanguageProvider/selectors";
import { appLocales } from "../../i18n";

const LanguageSwitch = styled.div`
  display: flex;
  align-items: flex-end;
  justify-content: flex-end;
`;

const Button = styled.button`
  margin: 0;
  padding: 0;
  color: ${(props) => props.theme.primaryColor};

  &:hover {
    text-decoration: underline;
  }
  &:disabled {
    text-decoration: none;
    color: ${(props) => props.theme.fontColor};
    font-weight: 500;
  }
  &:focus {
    outline: none;
  }
`;

const Separator = styled.span`
  padding: 0 10px;
`;

const languageLabels: Record<string, string> = {
  en: "English",
  de: "Deutsch"
};

interface LanguageStateProps {
  locale?: string;
}

interface LanguageDispatchProps {
  changeLocale: (language: string) => void;
}

type UpLanguageSwitchProps = LanguageStateProps & LanguageDispatchProps;

const UpLanguageSwitch = ({ locale, changeLocale }: UpLanguageSwitchProps) => {
  const { i18n } = useTranslation();

  const changeLanguage = (language: string) => {
    localStorage.setItem(LocalStorageKey.LANGUAGE, language);
    // Added additional language change for i18n
    i18n.changeLanguage(language);
    changeLocale(language);
  };

  return (
    <LanguageSwitch>
      {appLocales.map((item, index, locales) => (
        <Fragment key={item}>
          <Button
            type="button"
            disabled={locale === item}
            onClick={() => changeLanguage(item)}
          >
            <FormattedMessage
              id={`components.${item}`}
              defaultMessage={languageLabels[item]}
            />
          </Button>
          {locales.length - 1 !== index ? <Separator>|</Separator> : null}
        </Fragment>
      ))}
    </LanguageSwitch>
  );
};

const selectLocale: (state: LanguageRootState) => string = makeSelectLocale();

const mapStateToProps = createSelector(selectLocale, (locale): LanguageStateProps => ({ locale }));

const matchDispatchToProps = (dispatch: Dispatch): LanguageDispatchProps =>
  bindActionCreators({ changeLocale }, dispatch);

export default connect(mapStateToProps, matchDispatchToProps)(UpLanguageSwitch);
